import { create } from 'zustand';
import { useDocumentStore } from '@/store/documentStore';

export type EditorMode = 'normal' | 'editing' | 'connecting' | 'searching';

export type SaveStatus = 'saved' | 'saving' | 'offline' | 'error';

export interface EditorState {
  /** Currently selected node IDs. */
  selectedNodeIds: string[];
  /** Node currently being edited inline. */
  editingNodeId: string | null;
  /** Current editor mode. */
  mode: EditorMode;
  /** Viewport pan offset. */
  panX: number;
  panY: number;
  /** Viewport zoom level (1 = 100%). */
  zoom: number;
  /** Whether the toolbar is visible. */
  toolbarVisible: boolean;
  /** Source node for connection creation. */
  connectionSourceId: string | null;
  /** Current save status for the status bar. */
  saveStatus: SaveStatus;
  /** Which node's notes panel is open (null = closed). */
  notesPanelNodeId: string | null;
  /** Which node's attachment panel is open (null = closed). */
  attachmentPanelNodeId: string | null;
  /** Which node's AI panel is open (null = closed). */
  aiPanelNodeId: string | null;
  /** Current AI panel mode. */
  aiPanelMode: 'expand' | 'summarize' | 'question' | null;
  /** Which node's comment panel is open (null = closed). */
  commentPanelNodeId: string | null;
  /** Whether the context menu is currently open. */
  contextMenuOpen: boolean;
  /** Whether the node currently being edited is newly created (cancel on click-away). */
  isNewNode: boolean;
  /** Whether multi-select mode is active (clicking nodes toggles selection). */
  multiSelectMode: boolean;

  // Actions
  selectNode: (nodeId: string, multi?: boolean) => void;
  clearSelection: () => void;
  toggleMultiSelect: () => void;
  startEditing: (nodeId: string) => void;
  stopEditing: () => void;
  setMode: (mode: EditorMode) => void;
  setPan: (x: number, y: number) => void;
  setZoom: (zoom: number) => void;
  adjustZoom: (delta: number) => void;
  toggleToolbar: () => void;
  startConnecting: (sourceId: string) => void;
  cancelConnecting: () => void;
  setSaveStatus: (status: SaveStatus) => void;
  openNotesPanel: (nodeId: string) => void;
  closeNotesPanel: () => void;
  openAttachmentPanel: (nodeId: string) => void;
  closeAttachmentPanel: () => void;
  openAiPanel: (nodeId: string, mode?: 'expand' | 'summarize' | 'question') => void;
  closeAiPanel: () => void;
  openCommentPanel: (nodeId: string) => void;
  closeCommentPanel: () => void;
  setContextMenuOpen: (open: boolean) => void;
  setIsNewNode: (value: boolean) => void;
}

/**
 * If the currently-edited node is a new, completely empty node (no text, notes,
 * links, or attachments) and not the root, delete it from the document.
 * Called before clearing selection or switching to a different node so the
 * orphan is cleaned up even when InlineEditor unmounts without firing blur.
 */
function cleanupNewEmptyNode(state: EditorState): void {
  if (!state.isNewNode || !state.editingNodeId) return;
  const mapDoc = useDocumentStore.getState().mapDoc;
  const node = mapDoc.getNode(state.editingNodeId);
  if (!node) return;
  const meta = mapDoc.getMeta();
  const text = node.text?.trim() ?? '';
  const hasContent =
    text !== '' ||
    (node.notes != null && node.notes.trim() !== '') ||
    (node.links != null && node.links.length > 0) ||
    (node.attachments != null && node.attachments.length > 0);
  if (!hasContent && state.editingNodeId !== meta.rootNodeId && node.parentId) {
    mapDoc.deleteNode(state.editingNodeId);
  }
}

export const useEditorStore = create<EditorState>((set, get) => ({
  selectedNodeIds: [],
  editingNodeId: null,
  mode: 'normal',
  panX: 0,
  panY: 0,
  zoom: 1,
  toolbarVisible: true,
  connectionSourceId: null,
  saveStatus: 'saved',
  notesPanelNodeId: null,
  attachmentPanelNodeId: null,
  aiPanelNodeId: null,
  aiPanelMode: null,
  commentPanelNodeId: null,
  contextMenuOpen: false,
  isNewNode: false,
  multiSelectMode: false,

  selectNode: (nodeId, multi = false) => {
    const state = get();
    // If editing a different new node, clean it up before switching
    if (state.isNewNode && state.editingNodeId && state.editingNodeId !== nodeId) {
      cleanupNewEmptyNode(state);
    }
    set((prev) => ({
      selectedNodeIds: multi
        ? prev.selectedNodeIds.includes(nodeId)
          ? prev.selectedNodeIds.filter((id) => id !== nodeId)
          : [...prev.selectedNodeIds, nodeId]
        : [nodeId],
      // Stop editing the old new node when clicking a different node
      editingNodeId: prev.isNewNode && prev.editingNodeId !== nodeId ? null : prev.editingNodeId,
      mode: prev.isNewNode && prev.editingNodeId !== nodeId ? 'normal' as const : prev.mode,
      isNewNode: prev.isNewNode && prev.editingNodeId !== nodeId ? false : prev.isNewNode,
    }));
  },

  clearSelection: () => {
    cleanupNewEmptyNode(get());
    set({ selectedNodeIds: [], editingNodeId: null, mode: 'normal', isNewNode: false, contextMenuOpen: false });
  },

  toggleMultiSelect: () =>
    set((state) => ({ multiSelectMode: !state.multiSelectMode })),

  startEditing: (nodeId) =>
    set({ editingNodeId: nodeId, mode: 'editing', selectedNodeIds: [nodeId] }),

  stopEditing: () =>
    set({ editingNodeId: null, mode: 'normal', isNewNode: false }),

  setMode: (mode) => set({ mode }),

  setPan: (x, y) => set({ panX: x, panY: y }),

  setZoom: (zoom) =>
    set({ zoom: Math.max(0.1, Math.min(3, zoom)) }),

  adjustZoom: (delta) =>
    set((state) => ({ zoom: Math.max(0.1, Math.min(3, state.zoom + delta)) })),

  toggleToolbar: () =>
    set((state) => ({ toolbarVisible: !state.toolbarVisible })),

  startConnecting: (sourceId) =>
    set({ connectionSourceId: sourceId, mode: 'connecting' }),

  cancelConnecting: () =>
    set({ connectionSourceId: null, mode: 'normal' }),

  setSaveStatus: (status) => set({ saveStatus: status }),

  openNotesPanel: (nodeId) => set({ notesPanelNodeId: nodeId }),
  closeNotesPanel: () => set({ notesPanelNodeId: null }),

  openAttachmentPanel: (nodeId) => set({ attachmentPanelNodeId: nodeId }),
  closeAttachmentPanel: () => set({ attachmentPanelNodeId: null }),

  openAiPanel: (nodeId, mode) => set({ aiPanelNodeId: nodeId, aiPanelMode: mode ?? 'expand' }),
  closeAiPanel: () => set({ aiPanelNodeId: null, aiPanelMode: null }),

  openCommentPanel: (nodeId) => set({ commentPanelNodeId: nodeId }),
  closeCommentPanel: () => set({ commentPanelNodeId: null }),

  setContextMenuOpen: (open) => set({ contextMenuOpen: open }),

  setIsNewNode: (value) => set({ isNewNode: value }),
}));
